import type { ReactNode } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { teinteDuProjet } from '../apparence';
import { nomDuCompte, usePrenom } from '../profil';
import { useCompteDeLaBoite } from '../boite';
import {
  IconeBoite,
  IconeCalendrier,
  IconeDossier,
  IconeFeuilleCornee,
  IconeHorloge,
  IconePlus,
  IconeRouage,
  IconeSilhouette,
  IconeTableau,
} from '../partage/icones';
import { useDossiers } from '../partage/useArbre';
import {
  AGENDA,
  BIENTOT,
  BOITE_D_ENTREE,
  COMPTE_SANS_EMAIL,
  DEPLIER_LA_BARRE,
  LES_PAGES,
  MAINTENANT,
  MES_PROJETS,
  NEXTILT,
  NOUVEAU_PROJET,
  PARAMETRES,
  PROFIL,
  PROJETS,
  REPLIER_LA_BARRE,
  RESSOURCES,
  SORTIR,
  TABLEAU_DE_BORD,
  notesDansLaBoite,
} from '../partage/vocabulaire';
import { OUVRIR_LA_SAISIE_DU_PROJET } from '../partage/saisieDuProjet';
import { useBarreRepliee } from './barreRepliee';
import './BarreDeGauche.css';

/**
 * La barre de gauche de l'ordinateur (chantier `navigation`, 01, maquette du carnet
 * `accueil.html`, la barre `.gauche`). De haut en bas : le nom, les pages, « Mes projets » et
 * « Nouveau projet », puis le compte et « Sortir ». Elle remplace la barre du haut ; le
 * téléphone garde son cadre (`telephone/CadreDuTelephone.tsx`).
 *
 * Tableau de bord mène à `/accueil`, la carte de ta vie (02), où l'ordinateur s'ouvre.
 *
 * En bas, le prénom écrit dans Profil, sinon l'e-mail ; l'e-mail reste en infobulle (chantier
 * profil, ticket 03).
 *
 * Les icônes, au trait, celles de la maquette du carnet : `partage/icones.tsx`, où le rail des
 * angles d'un projet lit les mêmes (chantier angles-du-projet, ticket 04).
 *
 * Le « ‹ » en haut la replie en rail d'icônes, chaque nom en infobulle ; « › » la rouvre
 * (chantier `barre-repliable`, décision d'Igor du 2026-10-22). Repliée, « Sortir » n'y est pas :
 * on déplie pour sortir. Le choix est gardé sur l'appareil (`barreRepliee.ts`).
 *
 * « Boîte d'entrée », sous Maintenant (chantier boite-d-entree, ticket 02), porte le nombre de ses
 * notes, rien quand elle est vide ; repliée, le nombre reste, en pastille sur l'icône.
 */
const ICONE = 'gauche-icone';

export function BarreDeGauche({ email, onSortir }: { email: string | null; onSortir: () => void }) {
  const dossiers = useDossiers();
  const naviguer = useNavigate();
  const projets = dossiers.data?.dossiers ?? [];
  const prenom = usePrenom();
  const notes = useCompteDeLaBoite();
  const [repliee, basculer] = useBarreRepliee();
  const nom = nomDuCompte(prenom, email ?? COMPTE_SANS_EMAIL);
  const bulle = (mot: string) => (repliee ? mot : undefined);

  return (
    <aside className={repliee ? 'cadre-gauche cadre-gauche--repliee' : 'cadre-gauche'}>
      <div className="gauche-nom">
        <span className="gauche-marque" aria-hidden="true" />
        <span className="gauche-mot">{NEXTILT}</span>
        <button
          type="button"
          className="gauche-replier"
          onClick={basculer}
          aria-label={repliee ? DEPLIER_LA_BARRE : REPLIER_LA_BARRE}
          title={repliee ? DEPLIER_LA_BARRE : REPLIER_LA_BARRE}
          aria-expanded={!repliee}
        >
          {repliee ? '›' : '‹'}
        </button>
      </div>

      {/* data-tuto : les repères par lesquels le tuto trouve le menu (`zonesDuTuto.ts`). */}
      <nav className="gauche-pages" data-tuto="menu" aria-label={LES_PAGES}>
        <Page vers="/accueil" icone={<IconeTableau className={ICONE} />} mot={TABLEAU_DE_BORD} repliee={repliee} />
        <Page vers="/maintenant" icone={<IconeHorloge className={ICONE} />} mot={MAINTENANT} tuto="menu-maintenant" repliee={repliee} />
        <Page vers="/boite" icone={<IconeBoite className={ICONE} />} mot={BOITE_D_ENTREE} compte={notes} repliee={repliee} />
        <Page vers="/agenda" icone={<IconeCalendrier className={ICONE} />} mot={AGENDA} tuto="menu-agenda" repliee={repliee} />
        <Page vers="/dossiers" icone={<IconeDossier className={ICONE} />} mot={PROJETS} tuto="menu-projets" repliee={repliee} />
        <Bientot icone={<IconeFeuilleCornee className={ICONE} />} mot={RESSOURCES} repliee={repliee} />
        <Page vers="/profil" icone={<IconeSilhouette className={ICONE} />} mot={PROFIL} repliee={repliee} />
        <Page vers="/parametres" icone={<IconeRouage className={ICONE} />} mot={PARAMETRES} repliee={repliee} />
      </nav>

      <div className="gauche-titre">{MES_PROJETS}</div>
      <div className="gauche-projets">
        {projets.map((p) => (
          <NavLink key={p.id} to={`/feuille/${p.id}`} className="gauche-projet" title={p.titre}>
            <i className="gauche-pastille" style={{ ['--teinte' as string]: teinteDuProjet(p.couleur) }} />
            <span className="gauche-projet-nom">{p.titre}</span>
          </NavLink>
        ))}
        {/* La création d'aujourd'hui : la saisie du nom de la page Projets, ouverte (`NouveauProjet`). */}
        <button
          type="button"
          className="gauche-projet gauche-nouveau"
          onClick={() => naviguer('/dossiers', { state: OUVRIR_LA_SAISIE_DU_PROJET })}
          title={bulle(NOUVEAU_PROJET)}
          aria-label={bulle(NOUVEAU_PROJET)}
        >
          <IconePlus className={ICONE} />
          <span className="gauche-mot">{NOUVEAU_PROJET}</span>
        </button>
      </div>

      <div className="gauche-moi">
        {repliee ? (
          <span className="gauche-initiale" title={nom}>
            {nom.charAt(0).toLocaleUpperCase('fr')}
          </span>
        ) : (
          <>
            <span className="gauche-email" title={email ?? undefined}>
              {nom}
            </span>
            <button type="button" className="gauche-sortir" onClick={onSortir}>
              {SORTIR}
            </button>
          </>
        )}
      </div>
    </aside>
  );
}

type Ligne = { icone: ReactNode; mot: string; repliee: boolean };

/** Une page ; `compte`, un nombre à côté de son nom (les notes de la boîte), rien quand il est nul. */
function Page({
  vers,
  icone,
  mot,
  tuto,
  compte = null,
  repliee,
}: Ligne & { vers: string; tuto?: string; compte?: number | null }) {
  const nom = compte !== null ? `${mot} · ${notesDansLaBoite(compte)}` : mot;
  return (
    <NavLink
      to={vers}
      className="gauche-item"
      data-tuto={tuto}
      title={repliee ? nom : undefined}
      aria-label={repliee || compte !== null ? nom : undefined}
    >
      <span className="gauche-pic">{icone}</span>
      <span className="gauche-mot">{mot}</span>
      {compte !== null && (
        <small className="gauche-compte" aria-hidden="true">
          {compte}
        </small>
      )}
    </NavLink>
  );
}

/** Une page qui vient avec son chantier : marquée, et un clic ne mène nulle part. */
function Bientot({ icone, mot, repliee }: Ligne) {
  return (
    <span
      className="gauche-item gauche-item--bientot"
      aria-disabled="true"
      title={repliee ? `${mot} · ${BIENTOT}` : undefined}
    >
      <span className="gauche-pic">{icone}</span>
      <span className="gauche-mot">{mot}</span>
      <small className="gauche-bientot">{BIENTOT}</small>
    </span>
  );
}
